import {
  ReactFlowProvider,
  type ReactFlowInstance,
  type Rect,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { ReactNode } from 'react';

import type { OrgStructure } from '@commenda/api';
import { EmptyState } from '@commenda/pathfinder';

import {
  StructureChartAdapterHelper,
  type StructureChartKind,
} from './adapters';
import { StructureCanvas } from './components';
import { useStructureLayout } from './hooks';

export interface StructureChartProps {
  // Default: org.
  kind?: StructureChartKind;
  structure: OrgStructure;
  companyId: number;
  companyName?: string;
  onEntityClick?: (entityId: number) => void;
  onUnlinkedClick?: (entityId: number) => void;
  // Floating canvas chrome. Default: nothing rendered.
  topLeftSlot?: ReactNode;
  topRightSlot?: ReactNode;
  // Leaves out the minimap, legend, zoom and export controls, for offscreen captures.
  hideControls?: boolean;
  // Fires once the chart has settled, with the area a screenshot should frame.
  onReady?: (rf: ReactFlowInstance, contentBounds: Rect) => void;
}

const StructureChart = ({
  kind = 'org',
  structure,
  companyId,
  companyName,
  onEntityClick,
  onUnlinkedClick,
  topLeftSlot,
  topRightSlot,
  hideControls,
  onReady,
}: StructureChartProps) => {
  const adapter = StructureChartAdapterHelper.forKind(kind);
  const { model, frame, layout, flowNodes } = useStructureLayout(
    structure,
    adapter
  );
  const canvas = layout && flowNodes.length > 0 && (
    <StructureCanvas
      adapter={adapter}
      nodeSize={frame}
      layout={layout}
      relations={model.relations}
      flowNodes={flowNodes}
      companyId={companyId}
      companyName={companyName}
      onEntityClick={onEntityClick}
      onUnlinkedClick={onUnlinkedClick}
      hideControls={hideControls}
      onReady={onReady}
    />
  );

  return (
    <div className="flex h-full w-full flex-col gap-xl">
      <div className="relative flex-1 overflow-hidden rounded-sm border-[0.5px] border-border-primary bg-bg-light shadow-[inset_0px_2px_8px_0px_rgba(0,0,0,0.04)]">
        {/* Org and tax nodes differ in size, so switching starts a fresh viewport. */}
        <ReactFlowProvider key={kind}>
          {layout && flowNodes.length === 0 && (
            <EmptyState
              kind="first-use"
              size="section"
              title="No entities yet"
              description="Add an entity to start the structure chart."
            />
          )}
          {canvas &&
            (adapter.Provider ? (
              <adapter.Provider
                companyId={companyId}
                isInteractive={!hideControls}
              >
                {canvas}
              </adapter.Provider>
            ) : (
              canvas
            ))}
          {topLeftSlot && (
            // Inline top/left so spacing-token classes aren't dropped by the
            // consuming app's content scanner from this package.
            <div className="absolute z-10" style={{ top: 12, left: 12 }}>
              {topLeftSlot}
            </div>
          )}
          {topRightSlot && (
            <div className="absolute z-10" style={{ top: 12, right: 12 }}>
              {topRightSlot}
            </div>
          )}
        </ReactFlowProvider>
      </div>
    </div>
  );
};

export { StructureChart };
